import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { CookieProvider, useCookies } from './contexts/CookieContext';
import { ViewpointProvider } from './contexts/ViewpointContext';
import { LayoutProvider, useLayout, LAYOUTS } from './contexts/LayoutContext';
import Navigation from './components/shared/Navigation';
import Hero from './components/home/Hero';
import DigitalWorkers from './components/home/DigitalWorkers';
import DigitalWorkersV2 from './components/home/DigitalWorkers-v2';
import PlatformSection from './components/home/PlatformSection';
import AboutUs from './components/home/AboutUs';
import ContactSection from './components/home/ContactSection';
import Footer from './components/shared/Footer';
import SocialCard from './pages/SocialCard';
import Mentor from './pages/Mentor';
import CookieConsent from './components/common/CookieConsent';
import CookiePreferences from './components/common/CookiePreferences';
// Removed unused imports to satisfy linter
import LoginPage from './components/cms/LoginPage';

// 🎯 QOL Analytics Integration - Fire and Forget
import './analytics';

// Loading overlay component
const LoadingOverlay = ({ isVisible }) => {
  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#ffffff',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'opacity 0.3s ease-in-out'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #1890ff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      <p style={{
        color: '#666',
        fontSize: '16px',
        margin: 0,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        Loading...
      </p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const MainContent = () => {
  const { showConsent, handleAcceptAll, handleDeclineAll, handleSavePreferences } = useCookies();
  const { isContentReady } = useLanguage();
  const [showPreferences, setShowPreferences] = useState(false);
  const { currentLayout } = useLayout();
  const isLoaded = useRef(false);
  const initialWorker = useRef(null);

  useEffect(() => {
    // Handle initial route and worker
    const path = window.location.pathname;
    const match = path.match(/^\/digital-workers(?:\/([^/]+))?$/);
    
    if (match && match[1]) {
      initialWorker.current = match[1];
    }

    // Mark as loaded after a short delay to ensure rendering
    const timer = setTimeout(() => {
      isLoaded.current = true;
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <LoadingOverlay isVisible={!isContentReady} />
      <div style={{
        opacity: isContentReady ? 1 : 0,
        transition: 'opacity 0.5s ease-in-out',
        minHeight: '100vh'
      }}>
        <Navigation isLoaded={isLoaded} initialWorker={initialWorker.current} />
        <Hero />
        {currentLayout === LAYOUTS.V1 ? (
          <DigitalWorkers isLoaded={isLoaded} initialWorker={initialWorker.current} />
        ) : (
          <DigitalWorkersV2 isLoaded={isLoaded} initialWorker={initialWorker.current} />
        )}
        <PlatformSection />
        <AboutUs />
        <ContactSection />
        <Footer />
        {/* Temporarily hidden
        <DebugInfo />
        */}
        
        {showConsent && (
          <CookieConsent
            onAccept={handleAcceptAll}
            onDecline={handleDeclineAll}
            onManage={() => setShowPreferences(true)}
          />
        )}
        
        <CookiePreferences
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
          onSave={handleSavePreferences}
        />
      </div>
    </>
  );
};

const AppContent = () => {
  const path = window.location.pathname;

  // Render standalone pages if URL matches
  if (path === '/social-card') {
    return <SocialCard />;
  }
  if (path === '/mentor') {
    return <Mentor />;
  }
  if (path === '/fay/translations') {
    return <LoginPage />;
  }
  if (path === '/fay/analytics') {
    return <LoginPage initialTab="analytics" />;
  }

  return <MainContent />;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <CookieProvider>
          <ViewpointProvider>
            <LayoutProvider>
              <AppContent />
            </LayoutProvider>
          </ViewpointProvider>
        </CookieProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
